/**
 * Expediente: em que horas o profissional atende, e quando não atende.
 *
 * Duas coisas diferentes moram aqui, de propósito:
 *
 *  - a **semana**, que é a regra fixa ("abro terça a sábado, das 9 às 19, com
 *    almoço das 12 às 13"). Um dia sem faixa nenhuma é dia fechado;
 *  - as **folgas**, que são exceções datadas (feriado, dentista, viagem).
 *
 * O intervalo de almoço não é um campo próprio: é o buraco entre duas faixas.
 * Isso deixa o barbeiro representar qualquer jornada — inclusive as que não
 * cabem em "abre, almoça, fecha".
 */

import type { TimeOffView, WorkingHoursView } from '@barbearia/shared';
import { WEEKDAY_LABELS } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenHeader } from '../../App.tsx';
import { Button, ErrorNote, Field, Sheet, Spinner } from '../../components/ui.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { useSession, useTenant } from '../../lib/contexts.tsx';
import { longDateOf, todayIn } from '../../lib/format.ts';

interface StaffMember {
  id: string;
  name: string;
  role: string;
}

type Week = Record<number, Array<{ start: string; end: string }>>;

function emptyWeek(): Week {
  return { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };
}

function toWeek(ranges: WorkingHoursView[]): Week {
  const week = emptyWeek();
  for (const range of ranges) {
    week[range.weekday]?.push({ start: range.start, end: range.end });
  }
  for (const day of Object.keys(week)) {
    week[Number(day)]?.sort((a, b) => a.start.localeCompare(b.start));
  }
  return week;
}

export function WorkingHours() {
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();

  const isOwner = session?.membership?.role === 'OWNER';
  const [staffId, setStaffId] = useState<string>(session?.membership?.id ?? '');
  const [week, setWeek] = useState<Week>(emptyWeek());
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingTimeOff, setAddingTimeOff] = useState(false);

  const { data: staff } = useQuery({
    queryKey: ['staff'],
    queryFn: () => api.get<{ staff: StaffMember[] }>('/schedule/staff'),
    enabled: isOwner,
  });

  const selectedStaff = staff?.staff.find((member) => member.id === staffId);

  const { data, isPending } = useQuery({
    queryKey: ['working-hours', staffId],
    queryFn: () =>
      api.get<{ membershipId: string; ranges: WorkingHoursView[] }>('/schedule/working-hours', {
        membershipId: staffId || undefined,
      }),
  });

  useEffect(() => {
    if (!data) return;
    setWeek(toWeek(data.ranges));
    setDirty(false);
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const ranges = Object.entries(week).flatMap(([weekday, entries]) =>
        entries.map((entry) => ({ weekday: Number(weekday), start: entry.start, end: entry.end })),
      );
      return api.put('/schedule/working-hours', { ranges }, { membershipId: staffId || undefined });
    },
    onSuccess: async () => {
      setDirty(false);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['working-hours'] });
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  const change = (weekday: number, index: number, field: 'start' | 'end', value: string) => {
    setWeek((current) => {
      const entries = [...(current[weekday] ?? [])];
      const entry = entries[index];
      if (!entry) return current;
      entries[index] = { ...entry, [field]: value };
      return { ...current, [weekday]: entries };
    });
    setDirty(true);
  };

  const addRange = (weekday: number) => {
    setWeek((current) => {
      const entries = current[weekday] ?? [];
      const last = entries.at(-1);
      // A segunda faixa do dia começa depois da primeira: é o intervalo de
      // almoço, que é o caso comum. Ele ajusta se for outro.
      const next = last ? { start: '13:00', end: '19:00' } : { start: '09:00', end: '12:00' };
      return { ...current, [weekday]: [...entries, next] };
    });
    setDirty(true);
  };

  const removeRange = (weekday: number, index: number) => {
    setWeek((current) => ({
      ...current,
      [weekday]: (current[weekday] ?? []).filter((_, position) => position !== index),
    }));
    setDirty(true);
  };

  const copyToWeekdays = (weekday: number) => {
    setWeek((current) => {
      const source = current[weekday] ?? [];
      const next = { ...current };
      // Segunda a sexta. Sábado costuma ter horário próprio e domingo é fechado,
      // então copiar por cima deles atrapalharia mais do que ajudaria.
      for (const day of [1, 2, 3, 4, 5]) {
        next[day] = source.map((entry) => ({ ...entry }));
      }
      return next;
    });
    setDirty(true);
  };

  return (
    <div className="flex min-h-full flex-col pb-28">
      <ScreenHeader
        title="Expediente"
        subtitle="Horários, intervalos e folgas"
        action={
          <button
            type="button"
            onClick={() => navigate('/ajustes')}
            className="touch-target shrink-0 px-2 text-[15px] font-medium text-ink-soft"
          >
            Voltar
          </button>
        }
      />

      {isOwner && (staff?.staff.length ?? 0) > 1 && (
        <div className="flex gap-1.5 border-b border-line px-4 py-2">
          {staff?.staff.map((member) => (
            <button
              key={member.id}
              type="button"
              onClick={() => setStaffId(member.id)}
              className={`rounded-full px-3 py-1.5 text-[13px] font-medium ${
                staffId === member.id
                  ? 'bg-accent text-accent-ink'
                  : 'border border-line bg-surface-raised text-ink-soft'
              }`}
            >
              {member.name}
            </button>
          ))}
        </div>
      )}

      {isOwner && selectedStaff && staffId !== session?.membership?.id && (
        <StaffResetLink membershipId={staffId} name={selectedStaff.name} />
      )}

      {isPending && <Spinner />}

      <section className="px-4 pt-4">
        <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
          Semana
        </h2>

        <div className="flex flex-col gap-2">
          {[1, 2, 3, 4, 5, 6, 0].map((weekday) => {
            const entries = week[weekday] ?? [];
            const closed = entries.length === 0;

            return (
              <div
                key={weekday}
                className={`rounded-xl border px-4 py-3 ${
                  closed ? 'border-line bg-surface-sunken' : 'border-line bg-surface-raised'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[15px] font-medium text-ink">
                    {WEEKDAY_LABELS[weekday]}
                  </span>

                  {closed ? (
                    <button
                      type="button"
                      onClick={() => addRange(weekday)}
                      className="touch-target px-2 text-[14px] font-medium text-accent"
                    >
                      Abrir neste dia
                    </button>
                  ) : (
                    <span className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => addRange(weekday)}
                        className="touch-target px-2 text-[13px] font-medium text-accent"
                      >
                        + faixa
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setWeek((current) => ({ ...current, [weekday]: [] }));
                          setDirty(true);
                        }}
                        className="touch-target px-2 text-[13px] font-medium text-ink-faint"
                      >
                        fechar
                      </button>
                    </span>
                  )}
                </div>

                {closed ? (
                  <p className="pt-1 text-[13px] text-ink-faint">Não atende</p>
                ) : (
                  <div className="flex flex-col gap-2 pt-2">
                    {entries.map((entry, index) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: a faixa não tem identidade própria — a posição no dia é o que ela é
                        key={`${weekday}-${index}`}
                        className="flex items-center gap-2"
                      >
                        <input
                          type="time"
                          value={entry.start}
                          onChange={(event) => change(weekday, index, 'start', event.target.value)}
                          className="touch-target flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-[16px] tabular-nums text-ink"
                        />
                        <span className="text-ink-faint">às</span>
                        <input
                          type="time"
                          value={entry.end}
                          onChange={(event) => change(weekday, index, 'end', event.target.value)}
                          className="touch-target flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-[16px] tabular-nums text-ink"
                        />
                        <button
                          type="button"
                          aria-label="Remover faixa"
                          onClick={() => removeRange(weekday, index)}
                          className="touch-target w-10 shrink-0 text-[18px] text-ink-faint"
                        >
                          ×
                        </button>
                      </div>
                    ))}

                    {entries.length === 1 && (
                      <p className="text-[12px] text-ink-faint">
                        Para o intervalo de almoço, adicione uma segunda faixa: o buraco entre elas
                        é o almoço.
                      </p>
                    )}

                    {weekday >= 1 && weekday <= 5 && (
                      <button
                        type="button"
                        onClick={() => copyToWeekdays(weekday)}
                        className="self-start text-[13px] font-medium text-accent"
                      >
                        Repetir de segunda a sexta
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && <div className="pt-3">{<ErrorNote>{error}</ErrorNote>}</div>}
      </section>

      <TimeOffSection staffId={staffId} onAdd={() => setAddingTimeOff(true)} />

      {dirty && (
        <div className="pb-safe pointer-events-none sticky bottom-0 z-40 px-4 pt-2">
          <Button
            full
            className="pointer-events-auto shadow-lg"
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar expediente
          </Button>
        </div>
      )}

      {addingTimeOff && <TimeOffSheet staffId={staffId} onClose={() => setAddingTimeOff(false)} />}
    </div>
  );
}

/**
 * "O Rafa esqueceu a senha."
 *
 * Sem isto, a resposta era não haver resposta: o link de nova senha só existia
 * para cliente, e ninguém conseguia gerar o de quem trabalha na barbearia.
 *
 * Só o dono vê, e nunca para si mesmo — ele troca a própria senha em Sua conta,
 * já logado. O botão abre o WhatsApp com o texto pronto, igual ao da ficha do
 * cliente: o app não manda nada sozinho.
 */
function StaffResetLink({ membershipId, name }: { membershipId: string; name: string }) {
  const [error, setError] = useState<string | null>(null);

  const resetLink = useMutation({
    mutationFn: () =>
      api.post<{ whatsapp: { url: string } }>(`/schedule/staff/${membershipId}/reset-link`),
    onSuccess: (result) => {
      setError(null);
      window.open(result.whatsapp.url, '_blank');
    },
    onError: (cause) =>
      setError(cause instanceof ApiError ? cause.message : 'Não foi possível gerar o link'),
  });

  return (
    <div className="border-b border-line px-4 py-3">
      <button
        type="button"
        onClick={() => resetLink.mutate()}
        disabled={resetLink.isPending}
        className="touch-target text-[14px] font-medium text-accent disabled:opacity-50"
      >
        {resetLink.isPending ? 'Gerando…' : `Mandar nova senha para ${name.split(' ')[0]}`}
      </button>
      <p className="pt-1 text-[13px] text-ink-faint">
        Abre o WhatsApp com um link que vale por 1 hora. A senha atual continua valendo até ele
        criar a nova.
      </p>
      {error && <ErrorNote>{error}</ErrorNote>}
    </div>
  );
}

function TimeOffSection({ staffId, onAdd }: { staffId: string; onAdd: () => void }) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ['time-off'],
    queryFn: () => api.get<{ timeOffs: TimeOffView[] }>('/schedule/time-off'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/schedule/time-off/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['time-off'] });
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
    },
  });

  const mine = (data?.timeOffs ?? []).filter(
    (entry) => entry.membershipId === null || entry.membershipId === staffId,
  );

  return (
    <section className="px-4 pt-8">
      <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Folgas e feriados
      </h2>

      {mine.length === 0 && (
        <p className="pb-3 text-[14px] text-ink-soft">
          Nenhuma folga marcada. O que entrar aqui some da agenda — e nem você nem o cliente
          conseguem marcar por cima.
        </p>
      )}

      <ul className="flex flex-col gap-2">
        {mine.map((entry) => (
          <li
            key={entry.id}
            className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-raised px-4 py-3"
          >
            <span className="min-w-0">
              <span className="block truncate text-[15px] text-ink">
                {entry.reason ?? 'Bloqueado'}
              </span>
              <span className="block text-[13px] text-ink-faint">
                {longDateOf(entry.startsAt.slice(0, 10), tenant.timezone)}
                {entry.membershipId === null && ' · barbearia inteira'}
              </span>
            </span>
            <button
              type="button"
              onClick={() => remove.mutate(entry.id)}
              className="touch-target shrink-0 px-2 text-[14px] font-medium text-danger"
            >
              remover
            </button>
          </li>
        ))}
      </ul>

      <Button variant="secondary" full className="mt-3" onClick={onAdd}>
        Marcar folga
      </Button>
    </section>
  );
}

function TimeOffSheet({ staffId, onClose }: { staffId: string; onClose: () => void }) {
  const { tenant } = useTenant();
  const session = useSession();
  const queryClient = useQueryClient();

  const today = todayIn(tenant.timezone);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('14:00');
  const [reason, setReason] = useState('');
  const [wholeShop, setWholeShop] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOwner = session?.membership?.role === 'OWNER';

  const create = useMutation({
    mutationFn: () => {
      // Um dia inteiro vai da meia-noite à meia-noite do dia seguinte, no fuso
      // da barbearia — não do navegador de quem está mexendo.
      const startsAt = zoned(from, allDay ? '00:00' : startTime, tenant.timezone);
      const endsAt = allDay
        ? zoned(nextDay(to), '00:00', tenant.timezone)
        : zoned(to, endTime, tenant.timezone);

      return api.post('/schedule/time-off', {
        startsAt,
        endsAt,
        reason: reason || undefined,
        membershipId: wholeShop ? null : staffId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['time-off'] });
      await queryClient.invalidateQueries({ queryKey: ['agenda'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title="Marcar folga"
      footer={
        <Button full loading={create.isPending} onClick={() => create.mutate()}>
          Bloquear agenda
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="De"
            type="date"
            value={from}
            onChange={(event) => {
              setFrom(event.target.value);
              if (event.target.value > to) setTo(event.target.value);
            }}
          />
          <Field
            label="Até"
            type="date"
            value={to}
            min={from}
            onChange={(event) => setTo(event.target.value)}
          />
        </div>

        <label className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-raised px-4 py-3">
          <span className="text-[15px] text-ink">Dia inteiro</span>
          <input
            type="checkbox"
            checked={allDay}
            onChange={(event) => setAllDay(event.target.checked)}
            className="h-6 w-6 accent-[var(--color-primary)]"
          />
        </label>

        {!allDay && (
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Das"
              type="time"
              value={startTime}
              onChange={(event) => setStartTime(event.target.value)}
            />
            <Field
              label="Às"
              type="time"
              value={endTime}
              onChange={(event) => setEndTime(event.target.value)}
            />
          </div>
        )}

        <Field
          label="Motivo"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Dentista, feriado, viagem…"
          hint="Aparece na sua agenda, não para o cliente"
        />

        {isOwner && (
          <label className="flex items-center justify-between gap-3 rounded-xl border border-line bg-surface-raised px-4 py-3">
            <span className="min-w-0">
              <span className="block text-[15px] text-ink">Fechar a barbearia inteira</span>
              <span className="block text-[12px] text-ink-faint">
                Vale para todos os profissionais — use em feriado
              </span>
            </span>
            <input
              type="checkbox"
              checked={wholeShop}
              onChange={(event) => setWholeShop(event.target.checked)}
              className="h-6 w-6 shrink-0 accent-[var(--color-primary)]"
            />
          </label>
        )}

        {error && <ErrorNote>{error}</ErrorNote>}
      </div>
    </Sheet>
  );
}

/** 'AAAA-MM-DD' + 'HH:mm' no fuso da barbearia → instante ISO em UTC. */
function zoned(date: string, time: string, timezone: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const instant = new Date(`${date}T00:00:00Z`);

  const noon = new Date(`${date}T12:00:00Z`);
  const asLocal = new Date(noon.toLocaleString('en-US', { timeZone: timezone }));
  const offset = Math.round((noon.getTime() - asLocal.getTime()) / 60_000);

  instant.setUTCMinutes((hours ?? 0) * 60 + (minutes ?? 0) + offset);
  return instant.toISOString();
}

function nextDay(date: string): string {
  const next = new Date(`${date}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}
